import React, { Component, ErrorInfo, ReactNode } from 'react';

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Custom fallback UI to display on error */
  fallback?: ReactNode | ((error: Error, retry: () => void) => ReactNode);
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Callback when error boundary is reset */
  onReset?: () => void;
  /** Keys that trigger a reset when changed */
  resetKeys?: Array<string | number>;
  /** Error reporter service (e.g., Sentry) */
  errorReporter?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Custom logger */
  logger?: {
    error: (message: string, error: Error, extra?: unknown) => void;
  };
  /** Error level for different handling */
  level?: 'page' | 'section' | 'component';
  /** Show error details in production */
  showDetailsInProduction?: boolean;
  /** Enable retry functionality */
  enableRetry?: boolean;
  /** Custom class name for error UI */
  className?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
  resetKeys?: Array<string | number>;
}

// ============================================================================
// Main ErrorBoundary Component
// ============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      resetKeys: props.resetKeys,
    };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    // Reset error boundary if resetKeys change
    const hasResetKeysChanged = 
      props.resetKeys !== state.resetKeys &&
      JSON.stringify(props.resetKeys) !== JSON.stringify(state.resetKeys);

    if (hasResetKeysChanged && state.hasError) {
      return {
        hasError: false,
        error: null,
        errorInfo: null,
        errorCount: 0,
        resetKeys: props.resetKeys,
      };
    }

    return { resetKeys: props.resetKeys };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, errorReporter, logger } = this.props;
    
    // Update state with error details
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Log error
    if (logger) {
      logger.error('ErrorBoundary caught an error', error, {
        errorInfo,
        errorCount: this.state.errorCount + 1,
        level: this.props.level,
      });
    } else {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Report to error tracking service
    if (errorReporter) {
      errorReporter(error, errorInfo);
    }

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    const { onReset } = this.props;
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });

    if (onReset) {
      onReset();
    }
  };

  override render() {
    const { hasError, error } = this.state;
    const { children, fallback, level = 'component', enableRetry = true } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        if (typeof fallback === 'function') {
          return <>{fallback(error, this.handleReset)}</>;
        }
        return <>{fallback}</>;
      }

      // Use level-specific fallback
      switch (level) {
        case 'page':
          return (
            <PageErrorFallback 
              error={error} 
              onReset={enableRetry ? this.handleReset : undefined}
              showDetails={this.shouldShowDetails()}
            />
          );
        case 'section':
          return (
            <SectionErrorFallback 
              error={error} 
              onReset={enableRetry ? this.handleReset : undefined}
              showDetails={this.shouldShowDetails()}
            />
          );
        default:
          return (
            <ComponentErrorFallback 
              error={error} 
              onReset={enableRetry ? this.handleReset : undefined}
              showDetails={this.shouldShowDetails()}
            />
          );
      }
    }

    return children;
  }

  private shouldShowDetails(): boolean {
    const { showDetailsInProduction } = this.props;
    return import.meta.env.MODE === 'development' || showDetailsInProduction === true;
  }
}

// ============================================================================
// Specialized Error Boundaries
// ============================================================================

export class APIErrorBoundary extends Component<
  Omit<ErrorBoundaryProps, 'fallback'> & {
    onRetry?: () => void;
    fallback?: ReactNode;
  },
  ErrorBoundaryState
> {
  constructor(props: Omit<ErrorBoundaryProps, 'fallback'> & {
    onRetry?: () => void;
    fallback?: ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Check if it's an API-related error
    const isApiError = 
      error.message.includes('fetch') ||
      error.message.includes('axios') ||
      error.message.includes('Network') ||
      (error as unknown as {code?: string}).code === 'NETWORK_ERROR' ||
      (error as unknown as {status?: number}).status;

    if (isApiError) {
      console.warn('API Error caught:', error.message);
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  override render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (fallback) {
        return <>{fallback}</>;
      }

      return <NetworkErrorFallback error={error} onRetry={this.handleRetry} />;
    }

    return children;
  }
}

// ============================================================================
// Error Fallback Components
// ============================================================================

interface ErrorFallbackProps {
  error: Error | null;
  onReset?: () => void;
  showDetails?: boolean;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  onReset,
  showDetails = false 
}) => {
  return (
    <div 
      role="alert"
      aria-live="assertive"
      className="flex items-center justify-center p-4"
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <svg
            className="h-6 w-6 text-red-600 mr-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            Something went wrong
          </h2>
        </div>
        
        <p className="text-gray-600 mb-4">
          We encountered an unexpected error. Please try again.
        </p>

        {showDetails && error && (
          <details className="mb-4">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Error Details
            </summary>
            <div className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto max-h-32">
              <div className="font-mono text-red-600">{error.message}</div>
              {error.stack && (
                <div className="mt-2 text-gray-600 whitespace-pre-wrap">
                  <div className="font-semibold">Error Stack:</div>
                  {error.stack}
                </div>
              )}
            </div>
          </details>
        )}

        {onReset && (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onReset}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => window.location.href = '/dashboard'}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PageErrorFallback: React.FC<ErrorFallbackProps> = (props) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <ErrorFallback {...props} />
    </div>
  );
};

export const SectionErrorFallback: React.FC<ErrorFallbackProps> = (props) => {
  return (
    <div className="bg-gray-50 rounded-lg p-8">
      <ErrorFallback {...props} />
    </div>
  );
};

export const ComponentErrorFallback: React.FC<ErrorFallbackProps> = (props) => {
  return <ErrorFallback {...props} />;
};

export const NetworkErrorFallback: React.FC<{
  error?: Error | null;
  onRetry?: () => void;
}> = ({ error, onRetry }) => {
  const isOffline = !navigator.onLine;

  return (
    <div 
      role="alert"
      className="flex items-center justify-center p-4"
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <svg
            className="h-6 w-6 text-yellow-600 mr-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            Network Error
          </h2>
        </div>
        
        <p className="text-gray-600 mb-4">
          {isOffline 
            ? "You are offline. Please check your internet connection."
            : "Unable to connect to our servers. Please check your connection and try again."}
        </p>

        {error?.message.includes('429') && (
          <p className="text-sm text-yellow-600 mb-4">
            Too many requests. Please wait a moment before trying again.
          </p>
        )}

        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

export const PermissionErrorFallback: React.FC = () => {
  return (
    <div 
      role="alert"
      className="flex items-center justify-center p-4"
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <svg
            className="h-6 w-6 text-red-600 mr-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            Permission Denied
          </h2>
        </div>
        
        <p className="text-gray-600 mb-4">
          You don't have access to this resource. Please contact your administrator if you believe this is an error.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Go Back
          </button>
          <a
            href="/support"
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Default Export
// ============================================================================

export default ErrorBoundary;